import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

/**
 * Componente che costruisce una lista di elementi riordinabili, a partire da una lista e un componente di template
 *
 * @see https://github.com/atlassian/react-beautiful-dnd
 *
 * @param {Array} elements - la lista di elementi da riordinare
 * @param {function} listItemComponent - il componente di template per ogni elemento della lista
 * @param {function} onReorder - la funzione di callback per il riordinamento
 *
 */
// TODO: aggiungere stili configurabili, se necessario
export function DraggableList({
  elements = [],
  listItemComponent = (element) => <></>,
  onReorder = (reorderedList) => {}
}) {
  /**
   * Funzione per riordinare una lista spostando l'elemento dell'indice startIndex
   * all'indice endIndex
   *
   * @param {Array} list - la lista da riordinare
   * @param {number} startIndex - l'indice dell'elemento da spostare
   * @param {number} endIndex - l'indice dove spostare l'elemento
   *
   * @returns la lista riordinata
   */
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  const grid = 2

  /**
   * Funzione per ottenere lo stile di un singolo elemento nella lista.
   * Quando l'elemento viene trascinato, avrà uno sfondo grigio.
   * Altrimenti, avrà uno sfondo bianco.
   *
   * L'altezza dell'elemento è impostata a 50px.
   * La proprietà userSelect è impostata su 'none' per impedire all'utente di selezionare il testo nell'elemento.
   * draggableStyle viene sparso nell'oggetto stile per includere gli stili forniti da react-beautiful-dnd.
   *
   * @param {boolean} isDragging - Se l'elemento viene trascinato o meno
   * @param {object} draggableStyle - Gli stili forniti da react-beautiful-dnd quando l'elemento viene trascinato
   * @return {object} L'oggetto stile per l'elemento
   */
  const getItemStyle = (isDragging, draggableStyle) => ({
    // Basic styles for draggable items
    ...draggableStyle,
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 0 ${grid}px 0`,
    height: '50px', // Set a fixed height for items
    background: isDragging ? '#e0e0e0' : '#ffffff'
  })

  /**
   * Ritorna l'oggetto stile per l'intera lista.
   * La lista è scrollabile verticalmente se il contenuto supera l'altezza massima.
   *
   * @return {object} L'oggetto stile per la lista
   */
  const getListStyle = () => ({
    padding: grid,
    width: 280,
    maxHeight: window.innerHeight - 250,
    overflowY: 'auto'
  })

  /**
   * Funzione chiamata quando l'utente rilascia l'elemento trascinato.
   * Se l'elemento viene rilasciato all'esterno della lista, non fa nulla.
   * Altrimenti, riordina la lista in base all'indice source e all'indice destination dell'oggetto result
   * e chiama la funzione onReorder con la lista riordinata.
   *
   * @param {object} result - l'oggetto contenente le informazioni sulla drag and drop
   */
  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const items = reorder(elements, result.source.index, result.destination.index)
    onReorder(items)
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}>
            {elements.map((element, index) => (
              <Draggable
                key={'selectable-header-' + index}
                draggableId={'selectable-header-' + index}
                index={index}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
                    {listItemComponent(element)}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
